import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, ExternalLink, FileText, Calendar, Wrench, GripVertical } from "lucide-react";

export type PdfTarget = {
  reportId: number;
  weldId: string;
  reportNo: string;
  rev: string | null;
  reportDate: string | null;
};

type Props = {
  target: PdfTarget | null;
  onClose: () => void;
  formatDate: (iso: string | null) => string;
  widthPercent: number;
  onWidthChange: (n: number) => void;
  fileUrl: string;
};

const MIN_PCT = 25;
const MAX_PCT = 85;

/** Fixed right-side drawer hosting an <iframe> of the pre-signed PDF.
 *  Left edge has a drag handle for resizing between MIN_PCT..MAX_PCT % of
 *  viewport width. Parent owns the width state (so main content padding can
 *  match). Width persists to localStorage at the parent level. */
export function PdfDrawer({
  target,
  onClose,
  formatDate,
  widthPercent,
  onWidthChange,
  fileUrl,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const dragStart = useRef<{ x: number; widthAtStart: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!target) {
      setUrl(null);
      setErr(null);
      return;
    }

    setUrl(null);
    setErr(null);
    setUrl(fileUrl);
  }, [target, fileUrl]);

  // Chrome's built-in PDF viewer respects these URL fragments:
  //   view=FitH   — fit the page horizontally to the viewer width
  //   toolbar=0   — hide the toolbar
  //   navpanes=0  — hide the left navigation pane
  // The `reloadKey` in the iframe `key` prop forces a remount on drag-end so
  // the PDF re-fits to the new width (Chrome doesn't live-resize the rendering).
  const iframeSrc = useMemo(
    () => (url ? `${url}#view=FitH&toolbar=0&navpanes=0` : null),
    [url],
  );

  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, onClose]);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStart.current) return;
      const dx = dragStart.current.x - e.clientX; // dragging left = +dx = wider
      const delta = (dx / window.innerWidth) * 100;
      const next = Math.max(
        MIN_PCT,
        Math.min(MAX_PCT, dragStart.current.widthAtStart + delta),
      );
      onWidthChange(next);
    },
    [onWidthChange],
  );

  const onMouseUp = useCallback(() => {
    dragStart.current = null;
    setDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    // Force iframe remount so Chrome's PDF viewer re-applies the FitH fragment
    // at the new drawer width (the embedded viewer doesn't live-resize).
    setReloadKey((k) => k + 1);
  }, [onMouseMove]);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStart.current = { x: e.clientX, widthAtStart: widthPercent };
    setDragging(true);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // Double-click handle to reset to 55%
  const onHandleDoubleClick = () => onWidthChange(55);

  if (!target) return null;

  return createPortal(
    <aside
      data-pdf-drawer="true"
      className="fixed top-[60px] bottom-0 right-0 z-[100] bg-white border-l border-[#E2E5EA] shadow-[0_8px_24px_rgba(0,39,63,0.08)] flex flex-col pointer-events-auto"
      style={{ width: `${widthPercent}%`, pointerEvents: "auto" }}
    >
      {/* Resize handle */}
      <div
        onMouseDown={onMouseDown}
        onDoubleClick={onHandleDoubleClick}
        className={`absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 cursor-ew-resize z-40 group ${
          dragging ? "bg-hero-red" : "bg-transparent hover:bg-hero-red/40"
        } transition-colors`}
        title="Drag to resize — double-click to reset"
      >
        <span
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-[calc(50%-1px)] left-0 flex items-center justify-center w-5 h-8 rounded-md bg-white border border-[#E2E5EA] text-heritage-grey shadow-[0_1px_3px_rgba(0,0,0,0.06)] opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity ${
            dragging ? "opacity-100" : ""
          }`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-4 py-2.5 border-b border-[#E2E5EA] flex-shrink-0">
        <div className="flex items-center gap-4 flex-wrap min-w-0">
          <Field icon={<Wrench className="h-3.5 w-3.5" />} label="Weld" value={target.weldId} />
          <Field
            icon={<FileText className="h-3.5 w-3.5" />}
            label="Report"
            value={target.rev ? `${target.reportNo} Rev.${target.rev}` : target.reportNo}
          />
          <Field
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="Date"
            value={formatDate(target.reportDate)}
          />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] text-heritage-grey font-mono tabular-nums w-8 text-right">
            {Math.round(widthPercent)}%
          </span>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-medium text-text-grey hover:bg-page-bg rounded-md transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              New tab
            </a>
          )}
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-page-bg transition-colors"
            title="Close (Esc)"
          >
            <X className="h-4 w-4 text-text-grey" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 bg-page-bg">
        {err && <div className="p-6 text-[13px] text-readable-red">{err}</div>}
        {!url && !err && (
          <div className="flex items-center justify-center gap-2 py-20 text-[13px] text-heritage-grey">
            <Loader2 className="h-5 w-5 text-hero-red animate-spin" />
            Fetching PDF…
          </div>
        )}
        {iframeSrc && (
          <iframe
            key={reloadKey}
            src={iframeSrc}
            title="Original PDF"
            className={`w-full h-full border-0 ${dragging ? "pointer-events-none" : ""}`}
          />
        )}
      </div>
    </aside>,
    document.body,
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-heritage-grey">{icon}</span>
      <span className="text-[9px] font-semibold text-heritage-grey uppercase tracking-[0.06em]">
        {label}
      </span>
      <span className="text-[12px] font-semibold text-blueprint-blue truncate font-heading">
        {value}
      </span>
    </div>
  );
}
